import { createHook, createStore } from 'react-sweet-state';
import type { Action } from 'react-sweet-state';
import { fg } from '@confluence/feature-gating';
import {
	type SDKUnsupportedReasons,
	type createInstance as CreateLoomInstanceType,
} from '@loomhq/record-sdk';
import type { ApolloError } from 'apollo-client';

import type { SessionDataType } from '@confluence/session-data';

import { INITIALIZE_LOOM_RECORDER_SDK_INTERACTION_METRIC } from './perf.config';
import { getAuthToken } from './loomAuthData';
import { getLoomInstanceDataByEnvironment } from './getLoomInstanceDataByEnvironment';
import {
	type NoRecordingAccessReason,
	getNoRecordingAccessReason,
} from './getNoRecordingAcccessReason';

type InitializedLoomSDK = Awaited<ReturnType<typeof CreateLoomInstanceType>>;

export type StateType = {
	isInitializing: boolean;
	initializedLoomSDK: InitializedLoomSDK | null;
	_hasLoomSDKInitializationStarted: boolean;
	hasRecordingAccess: boolean;
	noRecordingAccessReason: NoRecordingAccessReason | undefined;
	authTokenError: ApolloError | Error | null | undefined;
	isLoomSDKSupportedError: SDKUnsupportedReasons | undefined;
};

type InitSDKParams = {
	cloudId: string;
	environment: SessionDataType['environment'];
	entryPointLocation: string;
};
type InitLoomSDKType = ({ cloudId }: InitSDKParams) => Action<StateType>;
type ActionsType = {
	initLoomSDK: InitLoomSDKType;
};

const initialState: StateType = {
	isInitializing: true,
	initializedLoomSDK: null,
	_hasLoomSDKInitializationStarted: false,
	hasRecordingAccess: false,
	noRecordingAccessReason: undefined,
	authTokenError: undefined,
	isLoomSDKSupportedError: undefined,
};

// https://dev.loom.com/docs/record-sdk/details/technical-notes

/**
 * Business policy: explicitly block Safari before invoking Loom SDK.
 *
 * Background on Loom SDK's `isSupported()` implementation:
 * - The allow-list includes Chrome/Edge/Brave, and also Safari + Firefox:
 *     var fe = ["Chrome","Chrome Headless","Edge","Brave","Firefox","Safari"];
 *
 * - BUT the truth is that Safari have many restrictions that often cause
 *     Safari (MediaRecorder GA but not yet supported by Loom)
 *
 * - `thirdPartyCookieSupport` is hard-coded to `true`:
 *     const t = !0; // always passed as "true"
 * - `hasMediaStreamsSupport` check is minimal:
 *     only verifies that `navigator.mediaDevices.getUserMedia` and
 *     `navigator.mediaDevices.getDisplayMedia` exist.
 *
 * Consequence:
 * - On modern Safari, `isSupported()` almost always reports `{ supported: true }`
 *   because:
 *     (a) they are in the allow-list,
 *     (b) cookies are assumed enabled (hard-coded),
 *     (c) MediaDevices APIs exist.
 * - In reality, SDK initialization often fails on Safari due to:
 *     • third-party cookies blocked (ITP / private browsing)
 *     • Storage Access API restrictions
 *     • MediaRecorder quirks / instability
 *
 * Therefore:
 * - We enforce a business-layer policy check (`getPolicyUnsupportedReason`) that
 *   marks Safari as `incompatible-browser` up-front.
 * - This is **browser-detection**, but here it's deliberate: Loom SDK’s own
 *   feature-detection is incomplete / hard-coded. A pure feature check cannot
 *   distinguish these failure cases yet.
 * - We still call `isSupported()` afterwards as a safeguard for truly unsupported
 *   environments (e.g. missing MediaDevices).
 */

function isSafari(): boolean {
	if (typeof navigator === 'undefined' || !navigator.userAgent) return false;

	const ua = navigator.userAgent;

	const isMobileSafari =
		/\bSafari\b/i.test(ua) &&
		/\bMobile\/\w+/i.test(ua) &&
		!/\bCriOS|EdgiOS|OPiOS|FxiOS\b/i.test(ua);

	const isDesktopSafari =
		/\bVersion\/\d+.*Safari\/\d+/i.test(ua) && !/\bChrome|Chromium|Edg|OPR\b/i.test(ua);

	return isMobileSafari || isDesktopSafari;
}
export function getPolicyUnsupportedReason(): SDKUnsupportedReasons | undefined {
	if (isSafari()) {
		return 'incompatible-browser' as SDKUnsupportedReasons;
	}
	return undefined;
}

const initSDK: InitLoomSDKType =
	({ cloudId, environment, entryPointLocation }) =>
	async ({ getState, setState }) => {
		if (getState().initializedLoomSDK || getState()._hasLoomSDKInitializationStarted) {
			// singleton: only want 1 Loom SDK instance
			return;
		}
		setState({ _hasLoomSDKInitializationStarted: true });
		INITIALIZE_LOOM_RECORDER_SDK_INTERACTION_METRIC.start();

		// 0) Policy check: block Safari early
		if (fg('confluence_loom_strict_browser_policy')) {
			const policyReason = getPolicyUnsupportedReason();
			if (policyReason) {
				setState({
					isLoomSDKSupportedError: policyReason,
					isInitializing: false,
				});
				INITIALIZE_LOOM_RECORDER_SDK_INTERACTION_METRIC.cancel();
				return;
			}
		}

		// 1) SDK capability check (still call isSupported() as a safeguard)
		/**
		 * ...
		 * - We still call `isSupported()` afterwards as a safeguard for truly unsupported
		 *   environments (e.g. missing MediaDevices).
		 * - Note: given cookies are assumed enabled in `isSupported()`, it may still
		 *   return `{ supported: true }` under ITP/private mode/Storage Access blocks.
		 */

		try {
			const { isSupported } = await import(
				/* webpackChunkName: "loadable-loomhq-record-sdk-is-supported" */ '@loomhq/record-sdk/is-supported'
			);
			const { supported, error: isLoomSDKSupportedError } = await isSupported();

			if (!supported) {
				setState({ isLoomSDKSupportedError, isInitializing: false });
				INITIALIZE_LOOM_RECORDER_SDK_INTERACTION_METRIC.cancel();
				return;
			}
		} catch {
			// If SDK isSupported import/exec fails: treat as incompatible browser
			setState({
				isLoomSDKSupportedError: 'incompatible-browser' as SDKUnsupportedReasons,
				isInitializing: false,
			});
			INITIALIZE_LOOM_RECORDER_SDK_INTERACTION_METRIC.cancel();
			return;
		}

		const { recorderToken, tokenError } = await getAuthToken();
		if (!recorderToken || tokenError) {
			const noRecordingAccessReason = await getNoRecordingAccessReason(tokenError);
			setState({
				isInitializing: false,
				noRecordingAccessReason,
				authTokenError: tokenError,
			});
			INITIALIZE_LOOM_RECORDER_SDK_INTERACTION_METRIC.cancel();
			return;
		}
		setState({ hasRecordingAccess: true });

		try {
			const { createInstance } = await import(
				/* webpackChunkName: "loadable-loomhq-record-sdk" */ '@loomhq/record-sdk'
			);

			const initializedLoomSDK = await createInstance(
				getLoomInstanceDataByEnvironment({
					environment,
					jws: recorderToken,
					siteId: cloudId,
					entryPointName: entryPointLocation,
				}),
			);

			setState({ initializedLoomSDK, isInitializing: false });
			INITIALIZE_LOOM_RECORDER_SDK_INTERACTION_METRIC.stop();
		} catch (err) {
			setState({
				isInitializing: false,
				authTokenError: err as Error,
			});
			INITIALIZE_LOOM_RECORDER_SDK_INTERACTION_METRIC.cancel();
		}
	};

const actions: ActionsType = {
	initLoomSDK: initSDK,
};

export const store = createStore<StateType, ActionsType>({
	initialState,
	actions,
	name: 'loomEntryPointGlobalStateStore',
});

export const useLoomEntryPointGlobalState = createHook(store);
